import { copyNode, deleteNode, findNode, moveNode } from "@/lib/tree/tree";
import { documentStore, uiStore, singularityStore } from "@/store/store";
import { findFirstChildOfElementBeingInlineEdited } from "@/lib/singularity/page-structure/api-for-global-block-inline-editing";
import lodash from "lodash";
import {
  startInlineEditing,
  stopInlineEditing
} from "@/lib/inline-editor/inline-editor";
import { forceNextTick, forceNextTickAsync } from "@/util/force-next-tick";
import { Tree, TreeNode } from "@/types/tree";

export const FUTURELAYER_HEADER_ELEMENT_TYPE = "FutureLayerElements\\Header";
export const FUTURELAYER_FOOTER_ELEMENT_TYPE = "FutureLayerElements\\Footer";
export const FUTURELAYER_SITELOGO_ELEMENT_TYPE =
  "FutureLayerElements\\SiteLogo";

export const ESSENTIAL_ELEMENTS_WP_MENU_ELEMENT_TYPE =
  "EssentialElements\\WpMenu";

export const BAREBONES_HEADERS_NAME = "Headers";
export const BAREBONES_FOOTERS_NAME = "Footers";

export async function startEditingHeaderOrFooterElement(
  elementId: TreeNode["id"]
) {
  // sleep for 2 seconds
  // await new Promise(resolve => setTimeout(resolve, 2000));

  const element = findNode(documentStore.document.tree, elementId);

  if (
    !element ||
    (element.data.type !== FUTURELAYER_HEADER_ELEMENT_TYPE &&
      element.data.type !== FUTURELAYER_FOOTER_ELEMENT_TYPE)
  ) {
    return;
  }

  const elementType =
    element.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE ? "header" : "footer";
  singularityStore.setHeaderAndFooterEditorPanelState({
    elementId: element.id,
    elementType: elementType
  });

  const blockId = lodash.get(element.data.properties, "content.content.block");

  if (blockId) {
    // Close previous inline editing session if it exists,
    // for example if the user was editing a header and then activated a footer.
    if (uiStore.isInlineEditing) {
      await stopInlineEditing();
    }

    await startInlineEditing({
      elementId: element.id,
      blockId,
      showBackdrop: false,
      selectElementOnClose: false,
      scrollToElementOnStart: false,
      escToSelectElement: false,
      forceSsrOnClose: true
    });
  }

  await forceNextTickAsync(() => {
    const childSectionId = findFirstChildOfElementBeingInlineEdited();
    if (childSectionId && childSectionId !== elementId) {
      uiStore.activateElement(childSectionId);
    }
  });
}

export function getHeaderNode(): TreeNode {
  const headerNode: TreeNode = {
    id: 0,
    data: {
      type: FUTURELAYER_HEADER_ELEMENT_TYPE,
      properties: null
    },
    children: []
  };

  if (singularityStore.headerAndFooterBlockIdsInWP.headerId) {
    headerNode.data.properties = {
      content: {
        content: {
          block: singularityStore.headerAndFooterBlockIdsInWP.headerId
        }
      }
    };
  }

  return headerNode;
}

export function getFooterNode(): TreeNode {
  const footerNode: TreeNode = {
    id: 0,
    data: {
      type: FUTURELAYER_FOOTER_ELEMENT_TYPE,
      properties: null
    },
    children: []
  };

  if (singularityStore.headerAndFooterBlockIdsInWP.footerId) {
    footerNode.data.properties = {
      content: {
        content: {
          block: singularityStore.headerAndFooterBlockIdsInWP.footerId
        }
      }
    };
  }

  return footerNode;
}

export function addHeaderAndFooterNodesToTree(treeToMutate: Tree) {
  console.log(
    "addHeaderAndFooterNodesToTree called, value of header and footer block IDs in store is",
    singularityStore.headerAndFooterBlockIdsInWP
  );

  const headerNode = getHeaderNode();
  const copiedHeader = copyNode(treeToMutate, headerNode);
  moveNode(treeToMutate, copiedHeader.id, treeToMutate.root.id, 0);

  const footerNode = getFooterNode();
  const copiedFooter = copyNode(treeToMutate, footerNode);
  moveNode(
    treeToMutate,
    copiedFooter.id,
    treeToMutate.root.id,
    treeToMutate.root.children.length
  );

  return treeToMutate;
}

export function removeHeaderAndFooterNodesFromTree(treeToMutate: Tree) {
  const headerNodeId = treeToMutate.root.children.find(
    child => child.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
  )?.id;

  const footerNodeId = treeToMutate.root.children.find(
    child => child.data.type === FUTURELAYER_FOOTER_ELEMENT_TYPE
  )?.id;

  if (headerNodeId) {
    deleteNode(treeToMutate, headerNodeId);
  }

  if (footerNodeId) {
    deleteNode(treeToMutate, footerNodeId);
  }

  return treeToMutate;
}

export function getElementIdsForHeaderAndFooterNodeInCurrentDocument() {
  const headerNode = documentStore.document.tree.root.children.find(
    child => child.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
  );
  const footerNode = documentStore.document.tree.root.children.find(
    child => child.data.type === FUTURELAYER_FOOTER_ELEMENT_TYPE
  );

  return {
    headerId: headerNode?.id ?? null,
    footerId: footerNode?.id ?? null
  };
}

export function isTreeEmptyExceptForHeaderAndFooter(tree: Tree): boolean {
  const nonHeaderFooterChildren = tree.root.children.filter(
    child =>
      child.data.type !== FUTURELAYER_HEADER_ELEMENT_TYPE &&
      child.data.type !== FUTURELAYER_FOOTER_ELEMENT_TYPE
  );
  return nonHeaderFooterChildren.length === 0;
}
